import React, { useEffect, useState } from 'react';

import {
	useCreateLinkMutation,
	useEditLinkMutation
} from '@features/project-management-tools/api/projectManagementApi';
import { MIX_PANEL_EVENTS } from '@features/project-management-tools/constents';

import InputField from '@/components/InputField';
import ModalContainer from '@/components/modals/ModalContainer';
import { toastError, toastSuccess } from '@/components/Toast';
import LoaderComponent from '@/components/LoaderComponent';
import { getLoadingState } from '@/utils/Util';
import CommonImage from '@/components/CommonImage';
import { useMixPanelTrakingEventMutation } from '@/api/commonApis';

const AddAndEditLinkModal = ({ onClose, onSubmitSuccess, selectedLinkToEdit, projectId }) => {
	const [linkDetails, setLinkDetails] = useState({ linkTitle: '', linkUrl: '' });
	const [errors, setErrors] = useState({});

	const [mixPanelTrakingEvent] = useMixPanelTrakingEventMutation();
	const [createLink, { isLoading: isCreatingLink }] = useCreateLinkMutation();
	const [editLink, { isLoading: isEditingLink }] = useEditLinkMutation();
	useEffect(() => {
		if (selectedLinkToEdit) {
			setLinkDetails({
				...selectedLinkToEdit,
				linkTitle: selectedLinkToEdit.title,
				linkUrl: selectedLinkToEdit.url
			});
		}
	}, [selectedLinkToEdit]);

	const validate = () => {
		const newErrors = {};
		if (!linkDetails.linkTitle) {
			newErrors.linkTitle = 'Link title is required';
		}
		if (!linkDetails.linkUrl) {
			newErrors.linkUrl = 'Link url is required';
		}
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		const isValid = validate();
		if (!isValid) {
			return;
		}
		const requestBody = {
			title: linkDetails.linkTitle,
			url: linkDetails.linkUrl,
			id: selectedLinkToEdit?.id || null
		};

		if (selectedLinkToEdit) {
			editLink({ body: requestBody, projectId })
				.unwrap()
				.then(() => {
					setLinkDetails({ linkTitle: '', linkUrl: '' });
					mixPanelTrakingEvent({
						type: MIX_PANEL_EVENTS.PROJECT_ADD_LINK,
						body: requestBody
					});
					toastSuccess('Link updated successfully');
					onSubmitSuccess && onSubmitSuccess();
					onClose();
				})
				.catch((error) => {
					toastError(error?.data?.error);
				});
		} else {
			createLink({ body: requestBody, projectId })
				.unwrap()
				.then(() => {
					setLinkDetails({ linkTitle: '', linkUrl: '' });
					mixPanelTrakingEvent({
						type: MIX_PANEL_EVENTS.PROJECT_ADD_LINK,
						body: requestBody
					});
					toastSuccess('Link created successfully');
					onSubmitSuccess && onSubmitSuccess();
					onClose();
				})
				.catch((error) => {
					toastError(error?.data?.error);
				});
		}
	};

	const handleChange = (e) => {
		setLinkDetails({ ...linkDetails, [e.target.name]: e.target.value });
		setErrors({
			...errors,
			[e.target.name]: ''
		});
	};

	const isLoading = getLoadingState([isCreatingLink, isEditingLink]);

	return (
		<LoaderComponent loading={isLoading}>
			<ModalContainer onCloseModal={onClose}>
				<div className="wraper container-fluid dashboard-container">
					<div className="popup-wrapper">
						<form className="common-popup products-upload-modal" onSubmit={handleSubmit}>
							<div className="common-popup-header d-flex justify-content-between">
								<div className="popup-tilte">
									<h3 className="semibold-16 mb-0">
										{selectedLinkToEdit ? 'Edit' : 'Add'} link
									</h3>
								</div>
								<div className="close-btn">
									<CommonImage alt="close" src="/icons/close.svg" onClick={onClose} />
								</div>
							</div>
							<div className="common-popup-body">
								<div className="row">
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<InputField
												labelTitle="Link title*"
												type="text"
												name="linkTitle"
												value={linkDetails.linkTitle}
												onChange={handleChange}
												withWrapper={false}
												errors={errors.linkTitle}
											/>
										</div>
									</div>
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<InputField
												labelTitle="Paste link here*"
												type="text"
												name="linkUrl"
												value={linkDetails.linkUrl}
												onChange={handleChange}
												withWrapper={false}
												errors={errors.linkUrl}
											/>
										</div>
									</div>
								</div>
							</div>
							<div className="modal-footer">
								<div className="footer-buttons text-right">
									<button className="button outline size32 ml-2" onClick={onClose}>
										Cancel
									</button>
									<button className="button size32 ml-2" type="submit">
										Submit
									</button>
								</div>
							</div>
						</form>
					</div>
				</div>
			</ModalContainer>
		</LoaderComponent>
	);
};

export default AddAndEditLinkModal;
